import { useEffect } from 'react';

import { useMyFeatureFlags } from '~/features/platform/hooks/use-feature-flags';
import { useAuthStore } from '~/store/auth.store';

/**
 * Live-update path for feature flags after the initial /auth/me seed.
 *
 * /auth/me populates `auth.user.featureFlags` on login + tenant switch (see
 * `mapMeResponseToAuthUser`). This hook subscribes to /feature-flags/me so
 * admin-side toggles reach the store without a re-login.
 *
 * Guard: skip the write when /feature-flags/me returns the same generation we
 * already have OR an empty payload while the store has flags. Both conditions
 * mean the sync is "stale" relative to /auth/me's seed and would clobber
 * correct data with empty/older data otherwise.
 *
 * Only runs while the user is authenticated.
 */
export function useFeatureFlagsSync(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setFeatureFlags = useAuthStore((s) => s.setFeatureFlags);
  const { data } = useMyFeatureFlags({ enabled: isAuthenticated });

  useEffect(() => {
    if (!data) return;

    const { user } = useAuthStore.getState();
    const currentGeneration = user?.flagsGeneration ?? '';
    const currentFlagCount = user ? Object.keys(user.featureFlags).length : 0;
    const incomingFlagCount = Object.keys(data.flags).length;

    // Same generation → already in sync, no-op.
    if (data.generation && data.generation === currentGeneration) return;

    // Empty payload while the store already has flags from /auth/me — likely
    // /feature-flags/me responded before its data caught up. Don't clobber.
    if (incomingFlagCount === 0 && currentFlagCount > 0) return;

    setFeatureFlags(data.flags, data.generation);
  }, [data, setFeatureFlags]);
}
